import { useRef, useState } from "react";
import api from "@/lib/api";
import { DataBrowser } from "@/components/DataBrowser";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, Trash2, Database } from "lucide-react";

export default function SystemDataTab({ clientId, services }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", description: "", service_id: "" });
  const [uploading, setUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a JSON or CSV file"); return; }
    if (!form.name) { toast.error("Dataset name is required"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name);
    fd.append("description", form.description);
    fd.append("service_id", form.service_id);
    setUploading(true);
    try {
      await api.post(`/clients/${clientId}/datasets`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dataset uploaded and parsed");
      setForm({ name: "", description: "", service_id: "" });
      if (fileRef.current) fileRef.current.value = "";
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const toggleVisible = (d, reload) =>
    api.post(`/datasets/${d.dataset_id}/visibility`, { client_visible: !d.client_visible })
      .then(() => { toast.success(d.client_visible ? "Hidden from client" : "Shared with client"); reload(); setReloadKey((k) => k + 1); })
      .catch(() => toast.error("Failed"));

  const remove = (d, reload) => {
    if (!window.confirm(`Delete dataset "${d.name}" and its records?`)) return;
    api.delete(`/datasets/${d.dataset_id}`).then(() => { toast.success("Deleted"); reload(); setReloadKey((k) => k + 1); })
      .catch(() => toast.error("Failed"));
  };

  return (
    <div className="space-y-5" data-testid="client-system-data">
      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1"><Database className="h-4 w-4 text-[#10B981]" />
          <h3 className="font-heading font-semibold text-[#0D3B2D]">Upload browseable data (JSON / CSV)</h3></div>
        <p className="text-xs text-[#4A635A] mb-4">Parsed into a read-only snapshot the client can browse in their portal. Stored isolated from ECOREXS systems. For SQL dumps, ZIPs or backups, use the Files tab.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Dataset name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Orders export" data-testid="dataset-name-input" className="bg-white border-[#E2EBF0]" /></div>
          <div><Label>Linked service (optional)</Label>
            <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}
              data-testid="dataset-service-select" className="w-full h-10 mt-1 bg-white border border-[#E2EBF0] rounded-md px-3 text-sm text-[#0D3B2D]">
              <option value="">None</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="dataset-desc-input" className="bg-white border-[#E2EBF0]" /></div>
          <div className="sm:col-span-2"><Label>File (.json or .csv)</Label>
            <input ref={fileRef} type="file" accept=".json,.csv" data-testid="dataset-file-input"
              className="block w-full text-sm text-[#4A635A] mt-1 file:mr-3 file:rounded-lg file:border-0 file:bg-[#0D3B2D] file:text-white file:px-3 file:py-2" />
          </div>
        </div>
        <Button onClick={upload} disabled={uploading} data-testid="dataset-upload-btn"
          className="mt-4 bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
          <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading…" : "Upload dataset"}
        </Button>
      </div>

      <DataBrowser
        listPath={`/clients/${clientId}/datasets`}
        recordsPath={(id) => `/datasets/${id}/records`}
        reloadKey={reloadKey}
        renderActions={(d, reload) => (
          <div className="flex items-center gap-3 text-xs">
            <button onClick={() => toggleVisible(d, reload)} data-testid={`dataset-visibility-${d.dataset_id}`}
              className={`flex items-center gap-1 ${d.client_visible ? "text-[#10B981]" : "text-[#4A635A]"}`}>
              {d.client_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {d.client_visible ? "Visible" : "Hidden"}
            </button>
            <button onClick={() => remove(d, reload)} data-testid={`dataset-delete-${d.dataset_id}`}
              className="flex items-center gap-1 text-red-600"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        )}
      />
    </div>
  );
}
